import { useEffect, useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2, Upload, Palette, Calendar as CalIcon } from "lucide-react";
import StepLayout from "./StepLayout";

const TIMEZONES = [
  "UTC",
  "Asia/Singapore", "Asia/Kuala_Lumpur", "Asia/Hong_Kong", "Asia/Tokyo",
  "Asia/Shanghai", "Asia/Kolkata", "Australia/Sydney",
  "Europe/London", "Europe/Paris",
  "America/New_York", "America/Los_Angeles",
];

/**
 * Step 2a — Company profile + Business settings (incl. data start date) +
 * Brand theme preview tile.
 *
 * Why "preview tile only" for theme: the full theme picker lives in
 * Settings → Brand Theme. Wizard surfaces a small swatch so the owner
 * knows the option exists, without making them pick mid-onboarding.
 */
export default function Step2aProfile({ stepIndex, totalSteps, onAdvance, onBack, onFinishLater }) {
  const { tenant, refresh } = useAuth();
  const [profile, setProfile] = useState({
    legal_company_name: "", tagline: "", address: "",
    company_email: "", company_phone: "", uen_number: "",
  });
  const [op, setOp] = useState({
    timezone: "Asia/Singapore",
    date_format: "DD/MM/YYYY",
    temperature_unit: "C",
    start_date: new Date().toISOString().slice(0, 10),
  });
  const [logoUrl, setLogoUrl] = useState("");
  const logoRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // Suppress validation errors until the user explicitly clicks Next once.
  // Prevents a transient pill from showing when arriving here straight
  // after Step 1's auto-continue.
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: o }] = await Promise.all([
          api.get("/tenant/profile"),
          api.get("/tenant/operational"),
        ]);
        const pp = p.profile || {};
        setProfile({
          legal_company_name: pp.legal_company_name || p.company_name || "",
          tagline: pp.tagline || "",
          address: pp.address || "",
          company_email: pp.company_email || "",
          company_phone: pp.company_phone || "",
          uen_number: pp.uen_number || "",
        });
        if (o?.operational) {
          setOp((s) => ({ ...s, ...o.operational, start_date: o.operational.start_date || s.start_date }));
        }
        setLogoUrl(tenant?.branding?.logo_url || "");
      } catch (e) { console.debug("step2a load failed", e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadLogo = async (file) => {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/tenant/branding/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(data.url);
      await refresh();
    } catch (ex) { setErr(formatErr(ex.response?.data?.detail)); }
    finally {
      setBusy(false);
      if (logoRef.current) logoRef.current.value = "";
    }
  };

  const submit = async () => {
    setTouched(true);
    setErr("");
    if (!profile.legal_company_name?.trim()) {
      return setErr("Legal company name is required.");
    }
    if (!profile.company_email?.trim()) {
      return setErr("Company contact email is required.");
    }
    if (!op.start_date) {
      return setErr("Data collection start date is required.");
    }
    setBusy(true);
    try {
      await api.put("/tenant/profile", profile);
      await api.put("/tenant/operational", op);
      await refresh();
      await onAdvance({ profile: true });
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const brand = tenant?.branding || {};
  const swatches = [
    { key: "primary_bg", label: "Background", color: brand.primary_bg || "#E7D7C8" },
    { key: "secondary_text", label: "Brand", color: brand.secondary_text || "#783D19" },
    { key: "accent_hover", label: "Accent", color: brand.accent_hover || "#2e630c" },
    { key: "warning", label: "Warning", color: brand.warning || "#f97316" },
    { key: "error", label: "Error", color: brand.error || "#ef4444" },
  ];

  return (
    <StepLayout
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="Your company at a glance"
      subtitle="The basics we'll print on reports, alerts, and the audit-ready PDFs."
      description={
        <div className="space-y-3">
          <p>Tell us who you are. We'll use this on every PDF, email alert, and audit summary the platform generates.</p>
          <p>The <strong>data collection start date</strong> is the floor for all historical logs — your dashboards and assessments begin tallying from this day forward.</p>
        </div>
      }
      illustration={
        <div className="vb-card p-4" style={{ background: "var(--vb-bg)" }}>
          <div className="flex items-center gap-2 mb-2">
            <Palette size={16} style={{ color: "var(--vb-text-2)" }} />
            <div className="vb-heading text-sm m-0" style={{ color: "var(--vb-text-2)" }}>Current brand theme</div>
          </div>
          <div className="flex gap-1.5 mb-2" data-testid="brand-swatch-row">
            {swatches.map((s) => (
              <div key={s.key} title={s.label}
                className="rounded h-6 flex-1"
                style={{ background: s.color, border: "1px solid var(--vb-border)" }} />
            ))}
          </div>
          <p className="text-[11px] opacity-60 m-0">
            Customise this anytime via Settings → Brand theme.
          </p>
        </div>
      }
      onBack={onBack}
      onNext={submit}
      onFinishLater={onFinishLater}
      busy={busy}
      errorMsg={touched ? err : ""}
    >
      <div className="space-y-5">
        {/* Logo + company core */}
        <div className="vb-card p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Building2 size={18} style={{ color: "var(--vb-text-2)" }} />
            <h2 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>Company profile</h2>
          </div>
          <div>
            <Label>Company logo (optional)</Label>
            <div className="flex items-center gap-4" data-testid="wiz-logo-row">
              <div className="rounded grid place-items-center overflow-hidden flex-shrink-0"
                style={{ width: 80, height: 80, border: "1px dashed var(--vb-border)", background: "var(--vb-bg-2)" }}>
                {logoUrl
                  ? <img src={logoUrl} alt="logo" className="w-full h-full object-contain" />
                  : <span className="text-xs opacity-50">No logo</span>}
              </div>
              <div>
                <input ref={logoRef} type="file" accept="image/*"
                  className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                <button type="button" className="vb-btn ghost sm"
                  onClick={() => logoRef.current?.click()} disabled={busy}
                  data-testid="wiz-logo-upload">
                  <Upload size={13} /> {logoUrl ? "Replace logo" : "Upload logo"}
                </button>
                <p className="text-[11px] opacity-60 mt-1.5 m-0">PNG/SVG · max 4MB</p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Legal company name *</Label>
              <input className="vb-input" required value={profile.legal_company_name}
                onChange={(e) => setProfile({ ...profile, legal_company_name: e.target.value })}
                placeholder="e.g. Grand Hyatt Singapore Pte Ltd"
                data-testid="wiz-legal-name" />
            </div>
            <div>
              <Label>UEN / registration no.</Label>
              <input className="vb-input" value={profile.uen_number}
                onChange={(e) => setProfile({ ...profile, uen_number: e.target.value })}
                placeholder="e.g. 199801234A" data-testid="wiz-uen" />
            </div>
          </div>
          <div>
            <Label>Tagline</Label>
            <input className="vb-input" value={profile.tagline}
              onChange={(e) => setProfile({ ...profile, tagline: e.target.value })}
              placeholder="A short brand line shown on reports & emails"
              data-testid="wiz-tagline" />
          </div>
          <div>
            <Label>Registered address</Label>
            <textarea className="vb-input" rows={2} value={profile.address}
              onChange={(e) => setProfile({ ...profile, address: e.target.value })}
              placeholder="Street, city, postal code, country"
              data-testid="wiz-address" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Contact email *</Label>
              <input type="email" required className="vb-input" value={profile.company_email}
                onChange={(e) => setProfile({ ...profile, company_email: e.target.value })}
                placeholder="hello@yourcompany.com" data-testid="wiz-email" />
            </div>
            <div>
              <Label>Contact phone</Label>
              <input className="vb-input" value={profile.company_phone}
                onChange={(e) => setProfile({ ...profile, company_phone: e.target.value })}
                placeholder="+65 ..." data-testid="wiz-phone" />
            </div>
          </div>
        </div>

        {/* Business settings */}
        <div className="vb-card p-6 space-y-4">
          <div className="flex items-center gap-2">
            <CalIcon size={18} style={{ color: "var(--vb-text-2)" }} />
            <h2 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>Business settings</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Business timezone</Label>
              <select className="vb-input" value={op.timezone}
                onChange={(e) => setOp({ ...op, timezone: e.target.value })}
                data-testid="wiz-tz">
                {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz.replace("_", " ")}</option>)}
              </select>
              <p className="text-[11px] opacity-60 mt-1 m-0">Drives end-of-day cut-off for daily checks.</p>
            </div>
            <div>
              <Label>Date format</Label>
              <select className="vb-input" value={op.date_format}
                onChange={(e) => setOp({ ...op, date_format: e.target.value })}
                data-testid="wiz-date-fmt">
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>
            <div>
              <Label>Default temperature unit</Label>
              <select className="vb-input" value={op.temperature_unit}
                onChange={(e) => setOp({ ...op, temperature_unit: e.target.value })}
                data-testid="wiz-temp-unit">
                <option value="C">°C (Celsius)</option>
                <option value="F">°F (Fahrenheit)</option>
              </select>
            </div>
            <div>
              <Label>Data collection start date *</Label>
              <input type="date" className="vb-input" required
                value={op.start_date || ""}
                onChange={(e) => setOp({ ...op, start_date: e.target.value })}
                data-testid="wiz-start-date" />
              <p className="text-[11px] opacity-60 mt-1 m-0">
                Dashboards begin tallying from here. You can pick a future date if you're setting up ahead of go-live — historical uploads must still land on or after this day.
              </p>
            </div>
          </div>
        </div>
      </div>
    </StepLayout>
  );
}

function Label({ children }) {
  return (
    <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>
      {children}
    </label>
  );
}
